/*
 * Product card image strips.
 *
 *   Mobile  -- native touch scrolling, one photo per swipe, plus arrows to step through.
 *              Snapping stays pure CSS and no script touches the gesture, so nothing here can
 *              interfere with the browser's momentum. The arrows are an addition to the
 *              swipe, not a replacement for it.
 *   Desktop -- hovering a card switches to the second photo, and arrows step through the rest.
 *
 * Hover switches instantly, matching the theme's original card behaviour (.onHover .off is a
 * plain opacity swap with no transition, so it has always been an instant change). Only the
 * arrows animate.
 *
 * Both breakpoints need the same thing from JavaScript: the images after the first are marked
 * loading="lazy", and a browser treats an image sitting outside a horizontal scroll
 * container's visible area as "not intersecting", so it defers the download indefinitely and
 * the customer lands on a blank slide. Flipping the attribute to "eager" is not reliable on
 * its own -- Safari does not always re-evaluate an image it has already parsed and deferred --
 * so the sources are re-applied to force the fetch. Each image is touched once.
 *
 * Where they differ is *when* that happens:
 *
 *   Mobile  -- as the card nears the viewport, because a swipe can come at any moment.
 *   Desktop -- only when the pointer first enters that card. Loading four images per card for
 *              100+ cards up front would be a serious weight increase on a page that
 *              previously downloaded two, and most cards are never hovered.
 */
(function () {
    var DESKTOP = '(min-width: 778px)';
    var SLIDE_MS = 320;

    function isDesktop() {
        return window.matchMedia && window.matchMedia(DESKTOP).matches;
    }

    function prefersReducedMotion() {
        return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    function stripOf(card) {
        return card.querySelector('.media x-grid');
    }

    /* ------------------------------------------------------------- image loading */

    function forceLoad(img) {
        if (img.getAttribute('data-swipe-loaded') === '1') return;
        img.setAttribute('data-swipe-loaded', '1');

        if ('fetchPriority' in img) img.fetchPriority = 'low';

        img.removeAttribute('loading');

        var picture = img.parentElement;
        if (picture && picture.tagName === 'PICTURE') {
            var sources = picture.querySelectorAll('source');
            for (var i = 0; i < sources.length; i++) {
                var sset = sources[i].getAttribute('srcset');
                if (sset) sources[i].setAttribute('srcset', sset);
            }
        }

        var srcset = img.getAttribute('srcset');
        if (srcset) img.setAttribute('srcset', srcset);

        var src = img.getAttribute('src');
        if (src) img.setAttribute('src', src);
    }

    function loadStripImages(card) {
        var strip = stripOf(card);
        if (!strip) return;

        var imgs = strip.querySelectorAll('img');
        for (var i = 0; i < imgs.length; i++) {
            if (imgs[i].complete && imgs[i].naturalWidth > 0) continue;
            forceLoad(imgs[i]);
        }
    }

    /* --------------------------------------------------------------- geometry */

    var CHEVRON_LEFT = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="15 4 7 12 15 20"></polyline></svg>';
    var CHEVRON_RIGHT = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="9 4 17 12 9 20"></polyline></svg>';

    function slides(strip) {
        var out = [];
        for (var i = 0; i < strip.children.length; i++) {
            if (window.getComputedStyle(strip.children[i]).display !== 'none') out.push(strip.children[i]);
        }
        return out;
    }

    /*
     * Slide positions are measured, not assumed.
     *
     * Desktop packs the slides edge to edge, so a slide's pitch is simply the strip's width.
     * Mobile puts a 6px gutter between them, so it is the width PLUS 6. Treating clientWidth
     * as the pitch on mobile leaves every step 6px short, and the shortfall accumulates: by
     * the fourth photo the strip is most of a gutter out and the arrow state goes wrong at
     * the end of the strip.
     *
     * Reading the real offsets covers both, and any future change to the gutter, for free.
     */
    function offsets(strip) {
        var s = slides(strip);
        var base = s.length ? s[0].offsetLeft : 0;
        var out = [];
        for (var i = 0; i < s.length; i++) out.push(s[i].offsetLeft - base);
        return out;
    }

    function currentIndex(strip) {
        var offs = offsets(strip);
        if (offs.length < 2) return 0;

        var pos = strip.scrollLeft;
        var best = 0;
        var bestGap = Math.abs(offs[0] - pos);
        for (var i = 1; i < offs.length; i++) {
            var gap = Math.abs(offs[i] - pos);
            if (gap < bestGap) { bestGap = gap; best = i; }
        }
        return best;
    }

    /* --------------------------------------------------------------- movement */

    function jumpTo(strip, left) {
        if (strip.__slideAnim) {
            cancelAnimationFrame(strip.__slideAnim);
            strip.__slideAnim = null;
        }
        strip.scrollLeft = left;
    }

    /*
     * Arrow movement on DESKTOP is animated here rather than with `scroll-behavior: smooth` or
     * scrollTo({behavior:'smooth'}).
     *
     * Both of those are no-ops in some environments -- and when CSS smooth scrolling is
     * inactive it swallows the scroll entirely rather than falling back to an instant jump,
     * so the arrow would appear to do nothing at all. Animating the scroll position directly
     * always moves the strip, whatever the browser's smooth-scroll support.
     *
     * If frames cannot run (a background tab, or reduced-motion preferences), it lands on the
     * target immediately instead of stalling part-way.
     */
    function animateTo(strip, left) {
        if (strip.__slideAnim) cancelAnimationFrame(strip.__slideAnim);

        var start = strip.scrollLeft;
        var delta = left - start;
        if (Math.abs(delta) < 1) return;

        if (prefersReducedMotion() || document.hidden || typeof requestAnimationFrame !== 'function') {
            jumpTo(strip, left);
            return;
        }

        var t0 = null;
        function step(now) {
            if (t0 === null) t0 = now;
            var p = Math.min(1, (now - t0) / SLIDE_MS);
            var eased = p < 0.5 ? 2 * p * p : 1 - Math.pow(-2 * p + 2, 2) / 2; // easeInOutQuad
            strip.scrollLeft = start + delta * eased;
            if (p < 1) {
                strip.__slideAnim = requestAnimationFrame(step);
            } else {
                strip.__slideAnim = null;
                strip.scrollLeft = left;
            }
        }
        strip.__slideAnim = requestAnimationFrame(step);
    }

    /*
     * MOBILE hands the movement to the browser instead.
     *
     * The mobile strip is a live touch scroller with `scroll-snap-type: x mandatory`. Driving
     * scrollLeft frame by frame across it means writing a run of positions that are not snap
     * points, which the engine is entitled to correct underneath the animation -- the result
     * fights itself and stutters. A single native scrollTo to an exact snap position is one
     * intent the browser can carry out cleanly, and it leaves momentum and snapping alone.
     */
    function nativeTo(strip, left) {
        if (typeof strip.scrollTo === 'function') {
            try {
                strip.scrollTo({ left: left, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
                return;
            } catch (e) {
                // Older Safari only accepts the two-argument form.
            }
        }
        strip.scrollLeft = left;
    }

    // `animate` is false for hover (an instant change, as the card has always behaved) and
    // true for the arrows.
    function goTo(strip, index, animate) {
        var offs = offsets(strip);
        if (!offs.length) return 0;

        if (index < 0) index = 0;
        if (index > offs.length - 1) index = offs.length - 1;

        var left = offs[index];

        if (!animate) {
            jumpTo(strip, left);
        } else if (isDesktop()) {
            animateTo(strip, left);
        } else {
            nativeTo(strip, left);
        }
        return index;
    }

    /* ---------------------------------------------------------------- arrows */

    function refreshArrows(card, index) {
        var strip = stripOf(card);
        if (!strip) return;

        var prev = card.querySelector('.card-nav.is-prev');
        var next = card.querySelector('.card-nav.is-next');
        if (!prev || !next) return;

        var count = slides(strip).length;
        var i = (typeof index === 'number') ? index : currentIndex(strip);

        prev.hidden = i <= 0;
        next.hidden = i >= count - 1;
    }

    function buildArrows(card) {
        if (card.getAttribute('data-card-nav') === '1') return;

        var strip = stripOf(card);
        if (!strip) return;

        // Nothing to step through.
        if (slides(strip).length < 2) return;

        card.setAttribute('data-card-nav', '1');

        var media = card.querySelector('.media');
        if (!media) return;

        function makeButton(dir) {
            var b = document.createElement('button');
            b.type = 'button';
            b.className = 'card-nav is-' + dir;
            b.innerHTML = dir === 'prev' ? CHEVRON_LEFT : CHEVRON_RIGHT;
            b.setAttribute('aria-label', dir === 'prev' ? 'Previous image' : 'Next image');

            // The whole photo sits inside a link to the product, so the click must be stopped
            // from reaching it -- otherwise pressing an arrow would navigate away. On touch
            // the same applies to the tap that precedes it.
            b.addEventListener('click', function (event) {
                event.preventDefault();
                event.stopPropagation();

                // Once the customer drives, hovering stops moving the strip for them.
                card.__navUsed = true;

                var target = goTo(strip, currentIndex(strip) + (dir === 'prev' ? -1 : 1), true);
                // Update from the intended index rather than the live scroll position, which
                // is still mid-animation at this point.
                refreshArrows(card, target);
            });

            return b;
        }

        media.appendChild(makeButton('prev'));
        media.appendChild(makeButton('next'));

        /*
         * On mobile the strip also moves by itself, under the customer's thumb, so the arrow
         * state has to follow the scroll rather than only the taps. Coalesced to one update
         * per frame -- scroll fires far more often than that during a flick.
         */
        strip.addEventListener('scroll', function () {
            if (strip.__navTick) return;
            strip.__navTick = (typeof requestAnimationFrame === 'function')
                ? requestAnimationFrame(function () { strip.__navTick = null; refreshArrows(card); })
                : setTimeout(function () { strip.__navTick = null; refreshArrows(card); }, 80);
        }, { passive: true });

        refreshArrows(card);
    }

    function activateCard(card) {
        if (!isDesktop()) return;
        loadStripImages(card);
        buildArrows(card);
    }

    /* --------------------------------------------------------------- mobile path */

    if ('IntersectionObserver' in window) {
        var observer = new IntersectionObserver(function (entries) {
            for (var i = 0; i < entries.length; i++) {
                if (!entries[i].isIntersecting) continue;
                var card = entries[i].target;
                observer.unobserve(card);

                // Desktop loads on hover instead, so nothing is fetched for cards the
                // customer never looks at.
                if (isDesktop()) continue;

                var strip = stripOf(card);
                if (strip && window.getComputedStyle(strip).overflowX === 'auto') {
                    loadStripImages(card);
                    // Built here rather than on first touch: an arrow that only appears after
                    // you have already interacted cannot tell you there is more to see, which
                    // is the whole reason for having it on a device that can swipe.
                    buildArrows(card);
                }
            }
        }, { rootMargin: '400px 0px' });

        var seen = typeof WeakSet === 'function' ? new WeakSet() : null;

        var scan = function () {
            var cards = document.querySelectorAll('product-card');
            for (var i = 0; i < cards.length; i++) {
                if (seen) {
                    if (seen.has(cards[i])) continue;
                    seen.add(cards[i]);
                }
                observer.observe(cards[i]);
            }
        };

        var scanTimer = null;
        var scanSoon = function () {
            if (scanTimer) return;
            scanTimer = setTimeout(function () { scanTimer = null; scan(); }, 250);
        };

        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', scan);
        } else {
            scan();
        }

        document.addEventListener('shopify:section:load', scanSoon);
        document.addEventListener('coretex:products:loaded', scanSoon);

        if ('MutationObserver' in window) {
            new MutationObserver(function (mutations) {
                for (var i = 0; i < mutations.length; i++) {
                    var added = mutations[i].addedNodes;
                    for (var j = 0; j < added.length; j++) {
                        var node = added[j];
                        if (node.nodeType !== 1) continue;
                        if (node.tagName === 'PRODUCT-CARD' ||
                            (node.querySelector && node.querySelector('product-card'))) {
                            scanSoon();
                            return;
                        }
                    }
                }
            }).observe(document.body || document.documentElement, { childList: true, subtree: true });
        }
    }

    /* -------------------------------------------------------------- desktop path */

    /*
     * Hover: show the second photo, exactly as the card has always done -- instantly, with no
     * animation. Leaving returns to the first photo.
     *
     * If the customer has used the arrows on a card, hover stops steering it, otherwise their
     * chosen photo would be pulled away the moment the pointer moved.
     */
    function onEnter(card) {
        if (!isDesktop()) return;
        activateCard(card);

        var strip = stripOf(card);
        if (!strip || card.__navUsed) return;
        if (slides(strip).length < 2) return;

        refreshArrows(card, goTo(strip, 1, false));
    }

    function onLeave(card) {
        if (!isDesktop()) return;

        var strip = stripOf(card);
        if (!strip) return;

        card.__navUsed = false;
        refreshArrows(card, goTo(strip, 0, false));
    }

    // Delegated so cards added by filtering or infinite scroll are covered with no re-init.
    document.addEventListener('mouseover', function (event) {
        var target = event.target;
        if (!target || !target.closest) return;
        var card = target.closest('product-card');
        if (!card) return;
        // Ignore moves between elements inside the same card.
        var from = event.relatedTarget;
        if (from && from.closest && from.closest('product-card') === card) return;
        onEnter(card);
    }, { passive: true });

    document.addEventListener('mouseout', function (event) {
        var target = event.target;
        if (!target || !target.closest) return;
        var card = target.closest('product-card');
        if (!card) return;
        var to = event.relatedTarget;
        if (to && to.closest && to.closest('product-card') === card) return;
        onLeave(card);
    }, { passive: true });

    // Keyboard users never fire mouseover.
    document.addEventListener('focusin', function (event) {
        var target = event.target;
        if (!target || !target.closest) return;
        var card = target.closest('product-card');
        if (card) activateCard(card);
    });

    // Keep the arrow state honest if the theme re-renders a card.
    document.addEventListener('shopify:section:load', function () {
        var cards = document.querySelectorAll('product-card[data-card-nav="1"]');
        for (var i = 0; i < cards.length; i++) refreshArrows(cards[i]);
    });

    /*
     * Rotating a phone, or crossing the breakpoint on a resizable window, changes both the
     * slide pitch and which behaviour applies. Re-measuring keeps the arrows pointing at the
     * right photo instead of wherever the old geometry said.
     */
    var reflowTimer = null;
    function reflow() {
        if (reflowTimer) clearTimeout(reflowTimer);
        reflowTimer = setTimeout(function () {
            var cards = document.querySelectorAll('product-card[data-card-nav="1"]');
            for (var i = 0; i < cards.length; i++) refreshArrows(cards[i]);
        }, 200);
    }

    window.addEventListener('resize', reflow);
    window.addEventListener('orientationchange', reflow);
})();
